import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Copy, Check, Gift, Loader2 } from 'lucide-react';
import { API } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';

const STATUS = { rewarded: ['Reward earned', 'bg-emerald-50 text-emerald-700'], waiting_for_payment: ['Waiting for first payment', 'bg-amber-50 text-amber-700'] };

// Settings → Refer & Earn: the workshop's own code and who has joined with it.
export const ReferEarn = () => {
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState('');
  useEffect(() => {
    axios.get(`${API}/subscription/referral`, { headers: authHeader() }).then((r) => setData(r.data)).catch(() => setErr('Could not load your referral code.'));
  }, []); // eslint-disable-line
  if (err) return <p className="text-sm text-red-700" data-testid="refer-error">{err}</p>;
  if (!data) return <div className="p-6 text-center text-slate-500"><Loader2 className="animate-spin inline" size={16} /></div>;
  const link = `${window.location.origin}/get-started?ref=${data.code}`;
  const copy = async (what, text) => {
    try { await navigator.clipboard.writeText(text); } catch {
      const ta = Object.assign(document.createElement('textarea'), { value: text });
      document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove();
    }
    setCopied(what); setTimeout(() => setCopied(''), 1500);
  };
  const p = data.program;
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid="refer-earn-section">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-teal-50 text-teal-600"><Gift size={18} /></div>
        <div>
          <h2 className="text-sm font-semibold text-teal-700 uppercase tracking-wider">Refer &amp; Earn</h2>
          {p.enabled ? (
            <p className="text-xs text-slate-500 mt-0.5" data-testid="refer-terms">A workshop that signs up with your code gets <b>{p.new_percent}% off</b> for {p.months} months. Once they make their first payment, you get <b>{p.referrer_percent}% off</b> your next payment.</p>
          ) : <p className="text-xs text-amber-700 mt-0.5" data-testid="refer-paused">Referral rewards are paused right now.</p>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-lg font-semibold tracking-wider border border-dashed border-teal-400 rounded-lg px-4 py-2 bg-teal-50/50" data-testid="refer-code">{data.code}</span>
        <button type="button" onClick={() => copy('code', data.code)} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-lg border border-slate-300 hover:border-slate-500" data-testid="refer-copy-code">{copied === 'code' ? <Check size={12} /> : <Copy size={12} />} Copy code</button>
        <button type="button" onClick={() => copy('link', link)} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700" data-testid="refer-copy-link">{copied === 'link' ? <Check size={12} /> : <Copy size={12} />} Copy sign-up link</button>
      </div>
      {data.rewards_available.length > 0 && (
        <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="refer-rewards">
          You have {data.rewards_available.length} reward{data.rewards_available.length > 1 ? 's' : ''} waiting — {data.rewards_available[0].percent}% off comes off your next payment.
        </p>
      )}
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">Workshops you referred</div>
        {data.referred.length === 0 ? <p className="text-sm text-slate-400" data-testid="refer-empty">No one has joined with your code yet.</p> : (
          <ul className="divide-y divide-slate-100" data-testid="refer-list">
            {data.referred.map((r, i) => (
              <li key={i} className="py-2 flex items-center justify-between text-sm" data-testid={`refer-row-${i}`}>
                <span>{r.workshop_name} <span className="text-xs text-slate-400">· joined {r.joined_on}</span></span>
                <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${STATUS[r.status][1]}`}>{STATUS[r.status][0]}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};
